import {DOCUMENT} from '@angular/common';
import {Directive, ElementRef, effect, inject, input} from '@angular/core';

/**
 * Publishes how far this element's top edge sits above the bottom of the viewport, as a CSS custom
 * property on the document root.
 *
 * It exists so that stylesheets can stack things against our fixed bottom furniture without hardcoding
 * its height.  Two elements sit down there -- the mobile secondary nav and the quiz ticker banner --
 * and both change height with the viewport: the nav stacks its icons above its labels on small screens,
 * and the banner wraps from one line to three between 1920px and 320px.  Every fixed offset written
 * against them has therefore been wrong at most widths (UKC-2924).
 *
 * The top edge rather than the height, because that composes: the banner already sits above the nav, so
 * a consumer clearing the banner needs no knowledge of the nav at all.
 *
 * The property is removed when the element goes away, so a rule can fall back with
 * `var(--name, <default>)` and consumers can gate on the element's presence with `:has()`.
 *
 * @example
 * <nav class="navbar fixed-bottom" appPublishTopEdge="--secondary-nav-top">
 */
@Directive({
  selector: '[appPublishTopEdge]',
})
export class PublishTopEdgeDirective {
  /** Name of the custom property to publish, including the leading double dash. */
  readonly property = input.required<string>({alias: 'appPublishTopEdge'});

  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly document = inject(DOCUMENT);

  constructor() {
    effect(onCleanup => {
      const property = this.property();
      const el = this.element.nativeElement;
      const root = this.document.documentElement;
      const view = this.document.defaultView;

      const measure = () => {
        const top = (view?.innerHeight ?? 0) - el.getBoundingClientRect().top;
        root.style.setProperty(property, `${Math.round(top)}px`);
      };

      measure();

      // ResizeObserver catches the element re-wrapping or restyling; the window listener catches it
      // moving without resizing, which is what happens when something appears beneath it.
      //
      // Feature-detected because jsdom has no ResizeObserver and components using this are unit
      // tested.  The initial measure() above still runs there.
      const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
      observer?.observe(el);
      view?.addEventListener('resize', measure);

      onCleanup(() => {
        observer?.disconnect();
        view?.removeEventListener('resize', measure);
        root.style.removeProperty(property);
      });
    });
  }
}
